import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    Radio,
    Spinner,
    Field,
    Combobox,
    Option,
    Text,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import {ICart, IPossibleUserCompanyMatch, ISharedContact} from '../../Api/Models/Cart';
import {
    useLazyGetExistingUserMatchQuery,
    useLazyGetPossibleUserMatchesQuery,
    useLazyGetShareableContactsQuery,
    useSaveCartMutation,
    useSharePreparedCartMutation,
} from '../../Api/PreparedCartEndpoints';
import { useLocalization } from '../Helpers/useLocalization';

interface IShareCartDialogProps {
    cart: ICart;
    preparedCartId?: string;
    open: boolean;
    onClose: () => void;
}

type ShareStep = 'loading' | 'pick-company' | 'pick-contacts' | 'saving' | 'success' | 'error';

const ShareCartDialog: React.FC<IShareCartDialogProps> = ({ cart, open, onClose, preparedCartId }) => {
    const navigate = useNavigate();
    const L = useLocalization();
    const [step, setStep] = React.useState<ShareStep>('loading');
    const [companyMatches, setCompanyMatches] = React.useState<IPossibleUserCompanyMatch[]>([]);
    const [contacts, setContacts] = React.useState<ISharedContact[]>([]);
    const [pendingCompanyId, setPendingCompanyId] = React.useState<string>('');
    const [pendingContactId, setPendingContactId] = React.useState<string>('');
    const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
    const [errorMsg, setErrorMsg] = React.useState<string>('');

    const [existingContact] = useLazyGetExistingUserMatchQuery();
    const [fetchUserMatches] = useLazyGetPossibleUserMatchesQuery();
    const [fetchContacts] = useLazyGetShareableContactsQuery();
    const [saveCart] = useSaveCartMutation();
    const [sharePreparedCart] = useSharePreparedCartMutation();

    const loadContacts = React.useCallback(async (companyId: string) => {
        if (!companyId) {
            console.log('no contact id. Bailing out')
            setStep('pick-contacts');
            return;
        }
        console.log('fetching contacts');
        const result = await fetchContacts({ companyId: companyId });
        if (result.error) {
            setErrorMsg(L('localization_cart_share_load_contacts_error'));
            setStep('error');
            return;
        }
        setContacts((result.data?.contacts ?? []).filter(a => a.id != pendingContactId));
        setStep('pick-contacts');
    }, [fetchContacts]);

    React.useEffect(() => {
        if (!open) {
            setStep('loading');
            setCompanyMatches([]);
            setContacts([]);
            setPendingCompanyId('');
            setPendingContactId('');
            setSelectedUserIds([]);
            setErrorMsg('');
            return;
        }

        const init = async () => {
            const result = preparedCartId ? await existingContact({preparedCartId}) : await fetchUserMatches();
            if (result.error || !result.data) {
                setErrorMsg(L('localization_cart_share_load_user_error'));
                setStep('error');
                return;
            }
            const matches: IPossibleUserCompanyMatch[] = result.data?.matches ?? [];
            setCompanyMatches(matches);
            if (matches.length > 1) {
                setStep('pick-company');
            } else {
                setPendingContactId(matches[0]?.contactId ?? '');
                await loadContacts(matches[0]?.companyId ?? '');
            }
        };

        init();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCompanyConfirm = async () => {
        await loadContacts(pendingCompanyId);
    };

    const handleSubmit = async () => {
        setStep('saving');
        if(!preparedCartId) {
            const items = [
                ...(cart.products?.map(p => ({
                    id: p.productId,
                    quantity: p.quantity,
                    price: p.price,
                    itemType: 'product' as const
                })) ?? []),
                ...(cart.bundles?.map(b => ({
                    id: b.bundleId,
                    quantity: b.quantity,
                    price: b.price,
                    itemType: 'bundle' as const
                })) ?? []),
            ];

            const saveResult = await saveCart({request: {items, comment: ''}});
            if ('error' in saveResult || !saveResult.data?.success) {
                setErrorMsg((saveResult as any)?.data?.error ?? L('localization_cart_share_save_error'));
                setStep('error');
                return;
            }

            const newCartId = saveResult.data?.id;
            if (!newCartId) {
                setErrorMsg(L('localization_cart_share_no_cart_id_error'));
                setStep('error');
                return;
            }
            if (selectedUserIds.length > 0) {
                await sharePreparedCart({ cartId: newCartId, request: { targetUsers: [...selectedUserIds, pendingContactId] } });
            }

            setStep('success');
            setTimeout(() => {
                onClose();
                navigate(`/prepared-cart/${newCartId}`);
            }, 2000);
        }
        else {
            if (selectedUserIds.length > 0) {
                await sharePreparedCart({ cartId: preparedCartId, request: { targetUsers: [...selectedUserIds, pendingContactId] } });
            }
            setStep('success');
            setTimeout(() => {
                onClose();
            }, 2000);
        }


    };
    
    const getValueString = () => {
        let value = '';
        selectedUserIds.map((a, i) => {
            const nameValue = contacts.find(c => c.id === a)?.name ?? ""
            value += i === 0 ? nameValue : `, ${nameValue}`
        })
        return value
    }

    const renderContent = () => {
        switch (step) {
            case 'loading':
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1em', padding: '1em 0' }}>
                        <Spinner size='small' />
                        <Text>{L('localization_cart_dialog_loading')}</Text>
                    </div>
                );
            case 'pick-company':
                return (
                    <>
                        <Text block style={{ marginBottom: '0.75em' }}>
                            {L('localization_cart_share_pick_company_prompt')}
                        </Text>
                        <Field label={L('localization_cart_share_company_label')}>
                            <RadioGroup
                                value={pendingCompanyId}
                                onChange={(_, data) => setPendingCompanyId(data.value)}
                            >
                                {companyMatches.map(match => (
                                    <Radio key={match.companyId} value={match.companyId} label={match.companyName} onSelect={() => setPendingContactId(match.contactId)} />
                                ))}
                            </RadioGroup>
                        </Field>
                    </>
                );
            case 'pick-contacts':
                return (
                    <>
                        <Text block style={{ marginBottom: '0.75em' }}>
                            {L('localization_cart_share_pick_contacts_prompt')}
                        </Text>
                        {contacts.length === 0 ? (
                            <Text style={{ color: '#888' }}>{L('localization_cart_share_no_contacts')}</Text>
                        ) : (
                            <Field label={L('localization_cart_share_contacts_label')}>
                                <Combobox
                                    multiselect
                                    value={getValueString()}
                                    placeholder={L('localization_cart_share_contacts_placeholder')}
                                    selectedOptions={selectedUserIds}
                                    onOptionSelect={(_, data) => {
                                        setSelectedUserIds(data.selectedOptions);
                                    }}
                                    style={{ width: '100%' }}
                                >
                                    {contacts.map(contact => (
                                        <Option
                                            key={contact.id} 
                                            value={contact.id}
                                        >
                                            
                                            {contact.name}
                                        </Option>
                                    ))}
                                </Combobox>
                            </Field>
                        )}
                    </>
                );
            case 'saving':
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1em', padding: '1em 0' }}>
                        <Spinner size='small' />
                        <Text>{L('localization_cart_share_saving')}</Text>
                    </div>
                );
            case 'error':
                return <Text style={{ color: 'red' }}>{errorMsg}</Text>;
            case 'success':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75em', padding: '1.5em 0' }}>
                        <CheckmarkCircleFilled style={{ fontSize: '3.5rem', color: '#107c10' }} />
                        <Text size={500} weight='semibold'>{L('localization_cart_share_success')}</Text>
                    </div>
                );
        }
    };

    const renderActions = () => {
        switch (step) {
            case 'pick-company':
                return (
                    <>
                        <Button
                            appearance='primary'
                            disabled={!pendingCompanyId}
                            onClick={handleCompanyConfirm}
                        >
                            {L('localization_cart_share_next')}
                        </Button>
                        <Button onClick={onClose}>{L('localization_common_cancel')}</Button>
                    </>
                );
            case 'pick-contacts':
                return (
                    <>
                        <Button appearance='primary' onClick={handleSubmit} disabled={selectedUserIds.length === 0}>
                            {L('localization_cart_share_cart')}
                        </Button>
                        <Button onClick={onClose}>{L('localization_common_cancel')}</Button>
                    </>
                );
            case 'error':
                return <Button onClick={onClose}>{L('localization_common_close')}</Button>;
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
            <DialogSurface style={{ minWidth: '400px' }}>
                <DialogBody>
                    <DialogTitle>{L('localization_cart_share_cart')}</DialogTitle>
                    <DialogContent style={{ padding: '1em 0' }}>
                        {renderContent()}
                    </DialogContent>
                    <DialogActions>
                        {renderActions()}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default ShareCartDialog;

